import React from 'react';
import {
  HISTORY_STATUS_CONFIG,
  formatDate,
  toHistoryKey,
  getLatestHistoryEntry,
  parseDatetimeLocalToUnix,
} from './useSensorDataTab.utils';
import S from './SensorDataTab.module.scss';

const pad2 = (value) => String(value).padStart(2, '0');

const unixToDatetimeLocal = (unixTs) => {
  const d = new Date(Number(unixTs) * 1000);
  if (!Number.isFinite(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mi = pad2(d.getMinutes());
  const ss = pad2(d.getSeconds());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
};

const DateTimeFilterInput = ({ item, applyValue }) => {
  const hasUnixValue = Number.isFinite(Number(item.value));
  const inputValue = hasUnixValue ? unixToDatetimeLocal(item.value) : '';

  return (
    <div className={S.inputWithClear}>
      <input
        type="datetime-local"
        step="1"
        className={S.dateInput}
        value={inputValue}
        onChange={(e) => {
          const nextTs = parseDatetimeLocalToUnix(e.target.value);
          applyValue({ ...item, value: nextTs ?? '' });
        }}
      />
      {item.value !== '' && item.value !== undefined && item.value !== null && (
        <button
          type="button"
          className={S.clearInputButton}
          title="Vider"
          onClick={() => applyValue({ ...item, value: '' })}
        >
          X
        </button>
      )}
    </div>
  );
};

const makeDateOperator = (label, value, compare) => ({
  label,
  value,
  InputComponent: DateTimeFilterInput,
  getApplyFilterFn: (filterItem) => {
    const filterTs = Number(filterItem.value);
    if (!Number.isFinite(filterTs)) return null;

    return (cellValue) => {
      // Depending on DataGrid internals/version, the callback can receive either
      // the raw cell value or an object carrying it.
      const rawValue =
        cellValue && typeof cellValue === 'object' && 'value' in cellValue
          ? cellValue.value
          : cellValue;
      const cellTs = Number(rawValue);
      return Number.isFinite(cellTs) && compare(cellTs, filterTs);
    };
  },
});

const DATE_FILTER_OPERATORS = [
  makeDateOperator('=', 'dateEq', (a, b) => a === b),
  makeDateOperator('≠', 'dateNeq', (a, b) => a !== b),
  makeDateOperator('>', 'dateGt', (a, b) => a > b),
  makeDateOperator('≥', 'dateGte', (a, b) => a >= b),
  makeDateOperator('<', 'dateLt', (a, b) => a < b),
  makeDateOperator('≤', 'dateLte', (a, b) => a <= b),
];

export const buildSensorDataColumns = ({
  handleDelete,
  getSensorNameById,
  getPositionLabel,
  historyMap,
  onOpenHistory,
  handleRestore,
}) => [
  {
    field: 'sensor_id',
    headerName: 'ID capteur',
    minWidth: 90,
    flex: 0.8,
    editable: false,
    headerAlign: 'left',
    align: 'left',
    lineBreak: 'anywhere',
    renderCell: ({ row, value }) => {
      const sensorId = Number(row?._sensorId ?? value);
      return getSensorNameById(sensorId);
    },
  },
  {
    field: 'pk_test',
    headerName: 'ID donnée',
    minWidth: 90,
    flex: 0.8,
    editable: false,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
  },
  {
    field: 'position_in_sensor',
    headerName: 'Composante',
    minWidth: 140,
    flex: 1,
    editable: false,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row }) => {
      const sensorId = Number(row?._sensorId ?? row?.sensor_id);
      const position = Number(row?.position_in_sensor);
      if (
        getPositionLabel &&
        Number.isFinite(sensorId) &&
        Number.isFinite(position)
      ) {
        return getPositionLabel(sensorId, position);
      }
      return Number.isFinite(position) ? `Pos. (${position})` : '—';
    },
  },
  {
    field: 'time_unix',
    headerName: 'Date / Heure',
    minWidth: 170,
    flex: 1.6,
    editable: true,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueFormatter: (value) => formatDate(value),
    filterOperators: DATE_FILTER_OPERATORS,
  },
  {
    field: 'value',
    headerName: 'Valeur',
    minWidth: 120,
    flex: 1,
    editable: true,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
  },
  {
    field: 'calc_value',
    headerName: 'Valeur calculée',
    minWidth: 145,
    flex: 1.2,
    editable: true,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
  },
  {
    field: 'update_type',
    headerName: 'État',
    minWidth: 130,
    flex: 1,
    editable: false,
    sortable: false,
    type: 'string',
    valueGetter: (value, row) => {
      const key = toHistoryKey(row._sensorId, row.pk_test);
      const entries = historyMap ? (historyMap[key] ?? []) : [];
      const lastEntry = getLatestHistoryEntry(entries);
      const config = lastEntry
        ? HISTORY_STATUS_CONFIG[lastEntry.update_type]
        : null;
      return config ? config.label : 'Non modifié';
    },
    renderCell: ({ row }) => {
      const key = toHistoryKey(row._sensorId, row.pk_test);
      const entries = historyMap ? (historyMap[key] ?? []) : [];
      const lastEntry = getLatestHistoryEntry(entries);
      const config = lastEntry
        ? HISTORY_STATUS_CONFIG[lastEntry.update_type]
        : null;
      const label = config ? config.label : 'Non modifié';

      return (
        <button
          type="button"
          className={S.btnEtatBadge}
          onClick={() => onOpenHistory(row, entries)}
          title="Voir l'historique"
        >
          <span className={config ? S[config.cssClass] : S.etatNonModifie}>
            {label}
          </span>
        </button>
      );
    },
  },
  {
    field: 'actions',
    headerName: 'Actions',
    minWidth: 95,
    flex: 0.8,
    sortable: false,
    filterable: false,
    renderCell: ({ id, row }) => {
      const key = toHistoryKey(row._sensorId, row.pk_test);
      const entries = historyMap ? (historyMap[key] ?? []) : [];
      const latestEntry = getLatestHistoryEntry(entries);

      if (latestEntry?.update_type === 'Delete') {
        return (
          <button
            type="button"
            className={S.actionRestoreButton}
            title="Restaurer"
            onClick={() => handleRestore(id)}
          >
            <span className={S.iconRestore}>↺</span>
          </button>
        );
      }

      return (
        <button
          type="button"
          className={S.actionDeleteButton}
          title="Supprimer"
          onClick={() => handleDelete(id)}
        >
          <span className={S.iconDelete}>✕</span>
        </button>
      );
    },
  },
];
